import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import type { NavParams } from '../nav';

/**
 * Purchasing copilot drawer — a thin client over POST /api/ai/agent.
 *
 * The agent is read-only by construction (its whole tool surface is blobs and
 * SELECTs, and it cannot reach the confidential playbooks), so this component
 * only has to carry the conversation and show which datasets were touched.
 *
 * Deliberately self-contained: it renders alongside whichever page is active,
 * so importing from a page would create a cycle the moment a page wanted the
 * drawer's state.
 */

/**
 * One page's prompt set. Every member is optional because `NO_PAGE` — the
 * stand-in for a tab with no entry at all — has none of them, and each read
 * below already falls back. `entity` is the union the two `ENTITY_HINT` keys
 * spell, so a page can never name a subject the hint map cannot answer for.
 */
interface PageSuggestions {
  entity?: 'vendor' | 'item';
  generic?: string[];
  vendor?: (account: string) => string[];
  item?: (itemId: string) => string[];
}

/**
 * Suggestions are limited to what the agent's six read tools — list_vendors,
 * get_vendor, get_item_price_series, list_price_events, list_agreements,
 * get_spend_summary — can actually answer. A prompt it must decline is worse
 * than no prompt, so nothing here needs a key-supplier flag, a QBR date or an
 * item-spend ranking: no tool serves those.
 *
 * `generic` prompts stand on their own. `vendor` / `item` prompts are only
 * offered when that context actually reached the drawer, and they name the
 * account or item outright — the copilot cannot see what is selected in a
 * dropdown on the page, so "this supplier" with no supplier attached is a
 * question the agent has no way to resolve.
 *
 * `entity` marks a page whose subject is chosen in its own on-page control. On
 * those pages, when no context arrived, the drawer says so instead of pretending
 * to know which supplier or item the user is looking at.
 */
/* `Partial<Record<…>>`, not `Record<…>`. A plain Record tells the compiler every
   string key is present, which makes the `?? NO_PAGE` at the use site read as dead
   code — and it is not dead: `App.tsx:37` registers a `contracts-register` tab, and
   `supplier360` and `coverage` are live tabs too, none of which have an entry here.
   The fallback fires in production every time somebody opens the Contracts register
   with the drawer up. Typing it `Partial` makes the index `PageSuggestions | undefined`,
   so the guard is visibly load-bearing and a later "remove the redundant ??" pass
   cannot quietly delete it. */
const SUGGESTIONS: Partial<Record<string, PageSuggestions>> = {
  spend: {
    generic: [
      'Which suppliers are our biggest by spend this financial year?',
      'How much of the trailing-12-month spend is non-PO?',
      'Which categories grew the most year on year?',
    ],
  },
  price: {
    entity: 'item',
    generic: [
      'Which agreements expire in the next 90 days, and at what price?',
      'Which categories carry the most spend this financial year?',
      'What price-change events are still open?',
    ],
    item: (itemId) => [
      `Why did the invoiced cost of item ${itemId} move?`,
      `Which agreements cover item ${itemId}, and at what price?`,
      `Show the monthly invoiced price of item ${itemId} over the last 24 months.`,
    ],
  },
  brief: {
    generic: [
      'What should we walk into the Supply Review worried about?',
      'Which agreements expire in the next 90 days?',
      'Which suppliers moved the most on spend year on year?',
    ],
  },
  relationships: {
    generic: [
      'Who are the top 10 suppliers by spend, with their tier?',
      'Which suppliers have open price-change events awaiting a decision?',
      'Which suppliers moved the most on spend year on year?',
    ],
  },
  supplier360: {
    entity: 'vendor',
    generic: [
      'Who are the top 10 suppliers by spend, with their tier?',
      'Which suppliers are below 90% SIFOT on material spend?',
      'What price-change events are still open?',
    ],
    vendor: (account) => [
      `How is ${account} performing on SIFOT and quality?`,
      `What price-change events are open with ${account}?`,
      `What agreements do we hold with ${account}?`,
    ],
  },
  performance: {
    generic: [
      'Which suppliers are below 90% SIFOT on material spend?',
      'Where are the NCRs concentrated?',
      'Which C and D tier suppliers carry the most spend?',
    ],
    vendor: (account) => [`How is ${account} performing on SIFOT and quality?`],
  },
  register: {
    generic: [
      'What price-change events are still open?',
      'What did we settle at versus what was asked?',
      'Which supplier has the most price events this year?',
    ],
    vendor: (account) => [`What price-change events are open with ${account}?`],
  },
  playbooks: {
    entity: 'vendor',
    generic: [
      'Which suppliers have open price-change events awaiting a decision?',
      'Which agreements expire in the next 90 days?',
      'Who are the top 10 suppliers by spend, with their tier?',
    ],
    vendor: (account) => [
      `What is our spend and service history with ${account}?`,
      `What agreements do we hold with ${account}, and when do they expire?`,
    ],
  },
  pipeline: {
    generic: [
      'Which categories carry the most spend?',
      'Which suppliers grew the most year on year?',
    ],
  },
  contracts: {
    generic: [
      'Which agreements expire in the next 90 days?',
      'What committed value sits behind those expiring agreements?',
      'Which suppliers do we hold the most purchase agreements with?',
    ],
    item: (itemId) => [`Which agreements cover item ${itemId}, and at what price?`],
  },
  governance: {
    generic: [
      'Which suppliers have open price events awaiting a decision?',
      'What is our total purchasing spend over the trailing 12 months?',
    ],
  },
  'data-sources': {
    generic: [
      'What is our total purchasing spend over the trailing 12 months?',
      'How much spend has no item match?',
    ],
  },
};

const DEFAULT_SUGGESTIONS = [
  'Who are our top suppliers by spend?',
  'Which agreements expire in the next 90 days?',
  'What price-change events are still open?',
];

/** Fallbacks for a page with no entity-specific prompts of its own. */
const VENDOR_FALLBACK = (account: string): string[] => [`Summarise ${account}: spend, service and open price events.`];
const ITEM_FALLBACK = (itemId: string): string[] => [`How has the invoiced price of item ${itemId} moved?`];

const MAX_SUGGESTIONS = 3;

/** Stable reference for an unknown tab, so the memo below isn't re-run every render. */
const NO_PAGE: PageSuggestions = {};

const ENTITY_HINT: Record<'vendor' | 'item', string> = {
  vendor: 'No supplier came through with the page, so name the one you mean — the copilot cannot see what is selected in the dropdown.',
  item: 'No item came through with the page, so quote its 6-digit item number — the copilot cannot see what is selected in the dropdown.',
};

/**
 * One turn of the transcript. `toolsUsed` is assistant-only — it is the dataset
 * provenance the route reports back — so it is optional, and the render below
 * is written to survive its absence on every user turn.
 */
interface CopilotMessage {
  role: 'user' | 'assistant';
  content: string;
  toolsUsed?: string[];
}

/** What actually goes on the wire. The route reads role and content and nothing
 *  else (`body.messages` in api/src/functions/aiAgent.ts), which is why `ask`
 *  maps `toolsUsed` off before posting. */
type CopilotWireMessage = Pick<CopilotMessage, 'role' | 'content'>;

/** The three fields the route's `systemPrompt` reads. Nav params carry more than
 *  this and none of the rest may be posted — see the comment on `vendorAccount`
 *  below. */
interface CopilotContext {
  page: string;
  vendorAccount?: string;
  itemId?: string;
}

/** `POST /ai/agent`: `ok({reply, toolsUsed})` on success and `{error}` on every
 *  refusal, including the 503 the AI endpoints degrade to when
 *  ANTHROPIC_API_KEY is unset. Every field is optional because the body is also
 *  `{}` when the response does not parse at all. */
interface AgentResponse {
  reply?: string;
  toolsUsed?: string[];
  error?: string;
}

/** `Error` carries no HTTP status, so the one `onError` singles the 503 out by
 *  is declared here rather than asserted onto a plain Error at the throw site. */
interface CopilotError extends Error {
  status?: number;
}

export interface CopilotProps {
  activeTab: string;
  params?: NavParams;
}

export default function Copilot({ activeTab, params = {} }: CopilotProps) {
  const apiFetch = useApi();
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [draft, setDraft] = useState('');
  // Errors live outside `messages` on purpose: they must never be replayed to
  // the model as prior assistant turns.
  const [failure, setFailure] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Only the two fields the agent reads. Nav params also carry page payloads
  // (the register's AI-extracted `proposal`, for one) that have no business
  // being posted to the AI endpoint.
  const vendorAccount = typeof params?.vendorAccount === 'string' ? params.vendorAccount.trim() : '';
  const itemId = params?.itemId == null ? '' : String(params.itemId).trim();

  const context = useMemo(() => {
    const out: CopilotContext = { page: activeTab };
    if (vendorAccount) out.vendorAccount = vendorAccount;
    if (itemId) out.itemId = itemId;
    return out;
  }, [activeTab, vendorAccount, itemId]);

  const page = SUGGESTIONS[activeTab] ?? NO_PAGE;

  const suggestions = useMemo(() => {
    const scoped = [
      ...(vendorAccount ? (page.vendor ?? VENDOR_FALLBACK)(vendorAccount) : []),
      ...(itemId ? (page.item ?? ITEM_FALLBACK)(itemId) : []),
    ];
    // De-duplicated: a scoped prompt can restate a generic one on some pages.
    return [...new Set([...scoped, ...(page.generic ?? DEFAULT_SUGGESTIONS)])].slice(0, MAX_SUGGESTIONS);
  }, [page, vendorAccount, itemId]);

  // Shown only where the page's subject lives in an on-page control that the
  // drawer never sees — being told to name it beats a prompt that cannot resolve.
  const contextHint = page.entity === 'vendor' && !vendorAccount ? ENTITY_HINT.vendor
    : page.entity === 'item' && !itemId ? ENTITY_HINT.item
      : null;

  const send = useMutation<AgentResponse, CopilotError, CopilotWireMessage[]>({
    mutationFn: async (history) => {
      const res = await apiFetch('/ai/agent', {
        method: 'POST',
        body: JSON.stringify({ messages: history, context }),
      });
      const body = (await res.json().catch(() => ({}))) as AgentResponse;
      if (!res.ok) {
        const err: CopilotError = new Error(body?.error ?? `Copilot request failed (${res.status})`);
        err.status = res.status;
        throw err;
      }
      return body;
    },
    onSuccess: (body) => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: body.reply || 'I did not get an answer back for that one — try rephrasing it.',
          toolsUsed: body.toolsUsed ?? [],
        },
      ]);
    },
    onError: (err) => {
      setFailure(
        err?.status === 503
          ? `${err.message} The copilot needs ANTHROPIC_API_KEY set on the purchasing Function App; every page still works without it.`
          : String(err?.message ?? err),
      );
    },
  });

  const ask = (text: string) => {
    const clean = String(text ?? '').trim();
    if (!clean || send.isPending) return;
    setFailure(null);
    const next: CopilotMessage[] = [...messages, { role: 'user', content: clean }];
    setMessages(next);
    setDraft('');
    send.mutate(next.map(({ role, content }) => ({ role, content })));
  };

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, failure, send.isPending]);

  return (
    <aside className="copilot" aria-label="Purchasing copilot">
      <div className="copilot-head">
        <span>✦ Copilot</span>
        <div style={{ flex: 1 }} />
        {messages.length > 0 && (
          <button
            className="btn btn-sm"
            onClick={() => { setMessages([]); setFailure(null); }}
            disabled={send.isPending}
          >
            Clear
          </button>
        )}
      </div>

      <div className="copilot-body" ref={bodyRef}>
        {messages.length === 0 && !failure && (
          <div className="state" style={{ padding: '20px 8px' }}>
            Ask about suppliers, spend, prices, agreements or the price-change register. Answers cite
            the dataset behind every number, and the copilot can only read — it never changes anything.
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`msg ${m.role === 'user' ? 'msg-user' : 'msg-ai'}`}>
            {m.content}
            {/* Was `m.toolsUsed?.length > 0`, which strict mode rejects. Spelled
                `(…?.length ?? 0) > 0` rather than `m.toolsUsed && m.toolsUsed.length > 0`
                because the `&&` form is NOT equivalent: it is the classic React trap
                where a falsy non-boolean left operand is returned and rendered, so a
                `toolsUsed` of 0 would paint a stray "0" into the bubble instead of
                nothing. This form always yields a boolean, and matches the original for
                every input including undefined and [].
                The `?.` on the join is only because a boolean guard does not narrow the
                type; the guard has already proved it is a non-empty array by then, so
                that optional chain can never actually short-circuit. */}
            {(m.toolsUsed?.length ?? 0) > 0 && (
              <span className="msg-src">sources: {m.toolsUsed?.join(', ')}</span>
            )}
          </div>
        ))}

        {send.isPending && <div className="msg msg-ai muted">Reading the data…</div>}

        {failure && <div className="msg msg-ai">{failure}</div>}
      </div>

      <div className="copilot-foot">
        {contextHint && (
          <div className="muted" style={{ fontSize: 11, lineHeight: 1.5 }}>{contextHint}</div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {suggestions.map((s) => (
            <button
              key={s}
              className="btn btn-sm"
              onClick={() => ask(s)}
              disabled={send.isPending}
              title={s}
              style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {s}
            </button>
          ))}
        </div>

        <form
          style={{ display: 'flex', gap: 6 }}
          onSubmit={(e) => { e.preventDefault(); ask(draft); }}
        >
          <input
            className="input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask about a supplier, item or agreement…"
            disabled={send.isPending}
            aria-label="Ask the copilot"
          />
          <button className="btn btn-primary" type="submit" disabled={send.isPending || !draft.trim()}>
            {send.isPending ? '…' : 'Ask'}
          </button>
        </form>
      </div>
    </aside>
  );
}
